import type { ValidationConfig } from "@/components/units/MediaUploader/mediaStore";

import { CollectionType, type MediaItemDto, type OptionItem } from "../types";

export interface Filter {
  id: string;
  type: string;
  value: string;
  group?: string;
}

export interface ICollection {
  id?: string;
  titleEn: string;
  titleAr: string;
  subtitleEn: string;
  subtitleAr: string;
  type: CollectionType;
  order: number | string;
  brands: OptionItem[];
  filters?: Filter[];
  groupBy?: string | null;
  active?: boolean;
  mediaItem?: MediaItemDto | null;
  mediaItemId?: string | null;
}

export interface CollectionSummary {
  id: string;
  title: string;
  brandsCount: number;
  order: number;
  active?: boolean;
}

export type CollectionsSummaryList = CollectionSummary[];

export const COLLECTION_MEDIA_ACCEPTED_IMAGE_TYPES = {
  "image/jpeg": [".jpeg", ".jpg"],
  "image/png": [".png"],
  "image/gif": [".gif"],
  "image/webp": [".webp"],
};

export const COLLECTION_MEDIA_ACCEPTED_VIDEO_TYPES = {
  "video/mp4": [".mp4"],
};

export const COLLECTION_MEDIA_ACCEPTED_TYPES = {
  ...COLLECTION_MEDIA_ACCEPTED_IMAGE_TYPES,
  ...COLLECTION_MEDIA_ACCEPTED_VIDEO_TYPES,
};

export const CollectionMediaValidation: ValidationConfig = {
  image: {
    acceptedMimeTypes: Object.keys(COLLECTION_MEDIA_ACCEPTED_IMAGE_TYPES),
    maxSize: 3 * 1024 * 1024,
    minSize: 1000,
    dimensionConstraints: {
      minWidth: 700,
      minHeight: 700,
    },
  },
  video: {
    acceptedMimeTypes: Object.keys(COLLECTION_MEDIA_ACCEPTED_VIDEO_TYPES),
    maxSize: 100 * 1024 * 1024,
    dimensionConstraints: {
      minWidth: 1080,
      minHeight: 1080,
    },
  },
  limits: {
    maxImages: 1,
    maxVideos: 1,
  },
};

export type ICollectionOrderList = { items: string[] };

export interface CollectionPreviewCuisine {
  id: string;
  value: string;
  icon: string | null;
}

export interface CollectionPreviewRestaurant {
  id: string;
  name: string;
  brandLogo: string | null;
  tags: string[];
  cuisines: CollectionPreviewCuisine[];
  mediaItems: MediaItemDto[] | null;
  rating: number | null;
  distance: number | null;
  eta: {
    durationSeconds: number;
    distanceMeters: number;
    mode: string;
  } | null;
}

export interface RestaurantsListCollectionPreview {
  title: string;
  subtitle: string | null;
  type: CollectionType.RESTUARANTS_LIST;
  order: number;
  items: CollectionPreviewRestaurant[];
}

export interface CollectionPreviewGroup {
  name: string;
  order?: number;
  iconUrl: string | null;
  filterId: string;
  filterValue: string | number | null;
}

export interface SelectableGroupCollectionPreview {
  title: string;
  subtitle: string | null;
  type: CollectionType.SELECTABLE_GROUP;
  order: number;
  mediaItem: MediaItemDto | null;
  filters: { id: string; value: string | number | null }[];
  groups: CollectionPreviewGroup[];
}

export type CollectionMobilePreview =
  | RestaurantsListCollectionPreview
  | SelectableGroupCollectionPreview;

export interface CollectionPreviewResponse {
  total: number;
  branches: OptionItem[];
  limit: number;
  offset: number;
  hasMore: boolean;
  preview: CollectionMobilePreview | null;
  uploadedMedia: MediaItemDto | null;
}

export interface CollectionCreateRequest {
  titleEn: string;
  titleAr: string;
  subtitleEn: string;
  subtitleAr: string;
  type: CollectionType;
  mediaItemId?: string | null;
  brands?: string[];
  filters?: Filter[];
  groupBy?: string | null;
}

export interface CollectionUpdateRequest
  extends Partial<CollectionCreateRequest> {
  active?: boolean;
}

export interface CollectionPreviewRequest extends CollectionCreateRequest {
  latitude?: number;
  longitude?: number;
  limit?: number;
  offset?: number;
}

export type SavedCollectionPreviewRequest = Pick<
  CollectionPreviewRequest,
  "latitude" | "longitude" | "limit" | "offset"
>;
